import { ChangeEvent, ReactElement, useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { useIntl, defineMessages, FormattedMessage } from 'react-intl'
import { useQuery } from '@apollo/client'
import styled from 'styled-components'

import { Column } from '@10xLabs/react-web/components/Column'
import { Row } from '@10xLabs/react-web/components/Row'
import { Autocomplete } from 'components/v2/Autocomplete'
import { Checkbox } from 'components/v2/Checkbox'
import { GET_SERVICES_BY_SEGMENTS } from 'features/Services/queries-legacy'
import { normalize } from 'utils/string'
import { Tag } from 'components/v2/Tag'

export const getValidRouteServicesBySegments = ({ segments, routeServices }: TSFixMe): TSFixMe => {
  return routeServices.filter(({ itinerary }: TSFixMe): TSFixMe => {
    const firstStop = itinerary?.[0]
    const lastStop = itinerary?.[itinerary.length - 1]

    return segments.find((segment: TSFixMe): TSFixMe => {
      return segment.origin.id === firstStop?.placeID && segment.destination.id === lastStop?.placeID
    })
  })
}

const messages = defineMessages({
  selectRouteServices: {
    id: 'fareRules.form.services.placeholder',
    defaultMessage: 'Select services'
  },
  empty: {
    id: 'fareRules.form.services.empty',
    defaultMessage: 'No services found'
  },
  monday: {
    id: 'formatted.date.monday',
    defaultMessage: 'Monday'
  },
  tuesday: {
    id: 'formatted.date.tuesday',
    defaultMessage: 'Tuesday'
  },
  wednesday: {
    id: 'formatted.date.wednesday',
    defaultMessage: 'Wednesday'
  },
  thursday: {
    id: 'formatted.date.thursday',
    defaultMessage: 'Thursday'
  },
  friday: {
    id: 'formatted.date.friday',
    defaultMessage: 'Friday'
  },
  saturday: {
    id: 'formatted.date.saturday',
    defaultMessage: 'Saturday'
  },
  sunday: {
    id: 'formatted.date.sunday',
    defaultMessage: 'Sunday'
  }
})

const useRouteServicesQuery = ({ segments, cityOrigin, cityDestination }: TSFixMe): TSFixMe => {
  const oneWaySegments = segments.filter((segment: TSFixMe): TSFixMe => {
    return segment.cityOrigin.id === cityOrigin.id
  })
  const { data: oneWayRouteServicesData, refetch: refetchOneWayRouteServices } = useQuery(GET_SERVICES_BY_SEGMENTS, {
    notifyOnNetworkStatusChange: true,
    skip: isEmpty(oneWaySegments),
    variables: {
      segmentIds: oneWaySegments.map(({ id }: TSFixMe): string => id)
    }
  })
  const returnSegments = segments.filter((segment: TSFixMe) => {
    return cityDestination.id !== cityOrigin.id && segment.cityOrigin.id === cityDestination.id
  })
  const { data: returnRouteServicesData, refetch: refetchReturnRouteServices } = useQuery(GET_SERVICES_BY_SEGMENTS, {
    notifyOnNetworkStatusChange: true,
    skip: isEmpty(returnSegments),
    variables: {
      segmentIds: returnSegments.map(({ id }: TSFixMe): string => id)
    }
  })

  const segmentsIds = segments.map(({ id }: TSFixMe): string => id).join('')
  useEffect(() => {
    if (!isEmpty(oneWaySegments)) {
      refetchOneWayRouteServices()
    }
    if (!isEmpty(returnSegments)) {
      refetchReturnRouteServices()
    }
  }, [segmentsIds])

  const oneWayRouteServices = oneWayRouteServicesData?.servicesWithPagination?.items || []
  const returnRouteServices = returnRouteServicesData?.servicesWithPagination?.items || []
  const routeServices = [
    ...oneWayRouteServices.map((routeService: TSFixMe): TSFixMe => {
      return {
        ...routeService,
        cityOrigin,
        cityDestination
      }
    }),
    ...returnRouteServices.map((routeService: TSFixMe): TSFixMe => {
      return {
        ...routeService,
        cityOrigin: cityDestination,
        cityDestination: cityOrigin
      }
    })
  ]

  return routeServices
}

const RouteServiceList = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const CheckboxContainer = styled.div<TSFixMe>`
  display: flex;
  align-items: center;
  margin: 5px 0;
  gap: 8px;
`

const getFilteredRouteServices = (values: TSFixMe, list: TSFixMe): TSFixMe => {
  return list.filter((routeService: TSFixMe): TSFixMe => {
    return isEmpty(values.find(({ id }: TSFixMe): TSFixMe => id === routeService.id))
  })
}

export const SelectRouteServices = (props: TSFixMe): TSFixMe => {
  const intl = useIntl()

  const [pattern, setPattern] = useState('')

  const {
    values,
    setValues,
    errors,
    touched,
    isShowingAllServicesCheckbox,
    allServicesAreSelected,
    setAllServicesAreSelected,
    cityOrigin,
    cityDestination,
    segments
  } = props
  const disabled = props.disabled || allServicesAreSelected
  const routeServices = useRouteServicesQuery({ cityOrigin, cityDestination, segments })
  const shownItems = getFilteredRouteServices(values, routeServices).filter((routeService: TSFixMe) => {
    return normalize(routeService.code).indexOf(normalize(pattern)) > -1
  })

  useEffect((): TSFixMe => {
    const shouldUpdateServices = allServicesAreSelected && values.length !== routeServices.length

    if (shouldUpdateServices) {
      setValues(routeServices)
    }
  }, [routeServices])

  const handleChange = (routeService: TSFixMe, { inputValue = '' } = {}): TSFixMe => {
    setPattern(inputValue)

    if (!isEmpty(routeService)) {
      setValues([...values, routeService])

      setPattern('')
    } else {
      setPattern(inputValue)
    }
  }

  const handleCheckboxChange = ({ target: { checked } }: ChangeEvent<HTMLInputElement>): void => {
    if (checked) {
      setAllServicesAreSelected?.(true)
      setValues(routeServices)
    } else {
      setAllServicesAreSelected?.(false)
      setValues([])
    }
  }

  return (
    <Row>
      <Column>
        {isShowingAllServicesCheckbox && (
          <CheckboxContainer>
            <Checkbox
              id="loyalty.campaing.rule.new.selectAll.id"
              checked={allServicesAreSelected}
              onChange={handleCheckboxChange}
            />
            <FormattedMessage id="inventoryRule.new.applyOnAllServices.label" defaultMessage="Apply on all services" />
          </CheckboxContainer>
        )}
        <Autocomplete
          {...props}
          placeholder={intl.formatMessage(messages.selectRouteServices)}
          items={shownItems}
          getItemLabel={({ code }: { code: string }): string => code}
          emptyMessage={intl.formatMessage(messages.empty)}
          onChange={handleChange}
          touched={touched}
          errors={errors}
          disabled={disabled}
        />
        {values.length > 0 && (
          <RouteServiceList>
            {values.map((routeService: TSFixMe, index: number): ReactElement => {
              return (
                <Tag
                  key={index}
                  isDeleteDisabled={props.disabled}
                  onDelete={async (): Promise<void> => {
                    await setAllServicesAreSelected?.(false)
                    setValues(values.filter(({ id }: { id: string }) => id !== routeService.id))
                  }}
                >
                  {routeService.code}
                </Tag>
              )
            })}
          </RouteServiceList>
        )}
      </Column>
    </Row>
  )
}
